import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface ButtonProps {
  children: React.ReactNode;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

export default function Button({
  children,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  fullWidth = false,
  type = 'button'
}: ButtonProps) {
  const location = useLocation();
  const navigate = useNavigate();
  
  const getVariantClasses = () => {
    switch(variant) {
      case 'secondary':
        return 'bg-gray-100 text-gray-800 hover:bg-gray-200';
      case 'outline':
        return 'bg-transparent border border-gray-300 text-gray-800 hover:bg-gray-50';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };
  
  const getSizeClasses = () => {
    switch(size) {
      case 'sm':
        return 'text-sm px-4 py-2';
      case 'lg':
        return 'text-lg px-8 py-4';
      default:
        return 'text-base px-6 py-3';
    }
  };
  
  const baseClasses = `
    inline-flex items-center justify-center
    rounded-2xl font-medium
    transition-all duration-300 ease-in-out
    ${fullWidth ? 'w-full' : ''}
    ${getVariantClasses()}
    ${getSizeClasses()}
    ${className}
  `;
  
  const motionProps = {
    whileHover: { scale: 1.02 },
    whileTap: { scale: 0.98 },
    transition: { duration: 0.2 }
  };
  
  if (href) {
    // Handle smooth scrolling for anchor links
    const handleClick = (e: React.MouseEvent) => {
      // Check if it's an anchor link
      if (href.includes('#')) {
        e.preventDefault();
        const targetId = href.split('#')[1];
        
        // If we're already on the home page
        if (location.pathname === '/' || location.pathname === '') {
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          // If we're on another page, navigate to home page with the hash
          navigate('/?scrollTo=' + targetId);
        }
      }
    };
    
    return (
      <motion.div {...motionProps}>
        <Link to={href} className={baseClasses} onClick={handleClick}>
          {children}
        </Link>
      </motion.div>
    );
  }
  
  return (
    <motion.button
      type={type}
      onClick={onClick}
      className={baseClasses}
      {...motionProps}
    >
      {children}
    </motion.button>
  );
}
